import { useState, useEffect, useCallback, useMemo } from 'react';
import { ingredientsService, IngredientStats } from '@/services/ingredients.service';
import { toast } from 'sonner';
import type {
  Ingredient,
  IngredientMovement,
  StockAlert,
  MovementType
} from '@/types/ingredient.types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

interface IngredientsFilters {
  search: string;
  category: string;
  sortBy: 'name' | 'value' | 'category' | 'supplier';
  sortOrder: 'asc' | 'desc';
}

interface StockAdjustmentData {
  type: MovementType;
  quantity: number;
  reason: string;
  locationId?: string;
  reference?: string;
}

export const useIngredients = () => {
  const [allIngredients, setAllIngredients] = useState<Ingredient[]>([]); // Store all ingredients
  const [movements, setMovements] = useState<IngredientMovement[]>([]);
  const [alerts] = useState<StockAlert[]>([]); // Mantenemos mock por ahora
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<IngredientsFilters>({
    search: '',
    category: 'all',
    sortBy: 'name',
    sortOrder: 'asc'
  });

  const itemsPerPage = 50;

  // Cargar todos los ingredientes una vez
  const loadAllIngredients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ingredientsService.getIngredients({
        page: 1,
        limit: 1000, // Load all ingredients
        includeInactive: true
      });

      setAllIngredients(response.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cargar ingredientes';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar ingredientes al montar el componente
  useEffect(() => {
    loadAllIngredients();
  }, [loadAllIngredients]);

  // Filtrar y ordenar ingredientes localmente
  const filteredIngredients = useMemo(() => {
    return allIngredients.filter(ingredient => {
      // Filtro por término de búsqueda
      const matchesSearch = !filters.search ||
        ingredient.name.toLowerCase().includes(filters.search.toLowerCase());

      // Filtro por categoría
      const matchesCategory = filters.category === 'all' ||
        ingredient.category === filters.category;

      return matchesSearch && matchesCategory;
    });
  }, [allIngredients, filters.search, filters.category]);

  // Ordenar ingredientes
  const sortedIngredients = useMemo(() => {
    const sorted = [...filteredIngredients].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (filters.sortBy) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'category':
          aValue = a.category;
          bValue = b.category;
          break;
        case 'supplier':
          aValue = a.supplier || '';
          bValue = b.supplier || '';
          break;
        case 'value':
          aValue = a.globalCostPerUnit || 0;
          bValue = b.globalCostPerUnit || 0;
          break;
        default:
          aValue = a.name;
          bValue = b.name;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return filters.sortOrder === 'asc' ? comparison : -comparison;
      }

      if (filters.sortOrder === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return sorted;
  }, [filteredIngredients, filters.sortBy, filters.sortOrder]);

  // Calcular estadísticas
  const stats = useMemo((): IngredientStats => {
    const totalValue = filteredIngredients.reduce((sum, ingredient) => {
      return sum + (ingredient.globalCostPerUnit || 0);
    }, 0);

    const categories = new Set(filteredIngredients.map(i => i.category)).size;

    return {
      totalItems: filteredIngredients.length,
      totalValue,
      lowStockItems: 0, // TODO: Calculate based on actual stock levels
      outOfStockItems: 0, // TODO: Calculate based on actual stock levels
      categories
    };
  }, [filteredIngredients]);

  // Paginación local
  const [currentPage, setCurrentPage] = useState(1);

  const paginatedIngredients = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedIngredients.slice(startIndex, endIndex);
  }, [sortedIngredients, currentPage, itemsPerPage]);

  const pagination = useMemo(() => {
    const totalPages = Math.ceil(sortedIngredients.length / itemsPerPage);
    return {
      currentPage,
      totalPages,
      totalItems: sortedIngredients.length,
      itemsPerPage,
      hasNextPage: currentPage < totalPages,
      hasPrevPage: currentPage > 1
    };
  }, [sortedIngredients.length, currentPage, itemsPerPage]);

  // Actualizar filtros
  const updateFilters = useCallback((newFilters: Partial<IngredientsFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  // Crear ingrediente
  const createIngredient = useCallback(async (data: Partial<Ingredient>) => {
    try {
      setLoading(true);

      const createData = {
        name: data.name || '',
        description: data.description,
        category: data.category || 'otros',
        unit: data.unit || 'unidad',
        supplier: data.supplier,
        globalCostPerUnit: 0, // Costo se maneja por separado en el modal de precios
        locationStocks: data.locationStocks
      };

      await ingredientsService.createIngredient(createData);

      toast.success('Ingrediente creado exitosamente');

      // Refresh data from API to get complete fields (categoryName, unitName, etc.)
      await loadAllIngredients();

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al crear ingrediente';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [loadAllIngredients]);

  // Actualizar ingrediente
  const updateIngredient = useCallback(async (id: string, data: Partial<Ingredient>) => {
    try {
      setLoading(true);

      const updateData = {
        name: data.name,
        description: data.description,
        category: data.category,
        unit: data.unit,
        supplier: data.supplier,
        locationStocks: data.locationStocks,
        isActive: data.isActive
      };

      await ingredientsService.updateIngredient(id, updateData);

      toast.success('Ingrediente actualizado exitosamente');

      // Refresh data from API to get complete fields (categoryName, unitName, etc.)
      await loadAllIngredients();

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al actualizar ingrediente';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [loadAllIngredients]);

  // Eliminar ingrediente
  const deleteIngredient = useCallback(async (id: string) => {
    try {
      setLoading(true);

      const response = await ingredientsService.deleteIngredient(id);

      // Remove ingredient from local state
      setAllIngredients(prev => prev.filter(ing => ing.id !== id));

      toast.success(response.message || 'Ingrediente eliminado exitosamente');

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al eliminar ingrediente';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Ajustar stock
  const adjustStock = useCallback(async (ingredientId: string, adjustment: StockAdjustmentData) => {
    try {
      setLoading(true);

      const response = await ingredientsService.adjustStock(ingredientId, {
        locationId: adjustment.locationId || 'loc-1',
        type: adjustment.type,
        quantity: adjustment.quantity,
        reason: adjustment.reason,
        reference: adjustment.reference
      });

      // Actualizar el ingrediente en la lista local
      setAllIngredients(prev => prev.map(ing =>
        ing.id === ingredientId ? response.data.ingredient : ing
      ));

      // Agregar el movimiento a la lista local
      setMovements(prev => [response.data.movement, ...prev]);

      toast.success(response.message || 'Stock ajustado exitosamente');

      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al ajustar stock';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar movimientos de un ingrediente
  const loadIngredientMovements = useCallback(async (ingredientId: string, filters?: {
    page?: number;
    limit?: number;
    type?: MovementType | 'all';
    dateFrom?: string;
    dateTo?: string;
  }) => {
    try {
      const response = await ingredientsService.getIngredientMovements(ingredientId, filters);
      setMovements(response.data);
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cargar movimientos';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Crear orden de compra (mantenemos la implementación existente por compatibilidad)
  const createPurchaseOrder = useCallback(async (items: any[]) => {
    try {
      setLoading(true);
      
      // Preparar datos para la API
      const orderData = {
        supplierName: 'Múltiple',
        items: items.map(item => ({
          ingredientId: item.ingredientId,
          ingredient: item.ingredient,
          quantity: item.quantity,
          unitCost: item.unitCost,
          totalCost: item.totalCost
        })),
        notes: `Orden generada automáticamente para reposición de stock bajo. Items: ${items.length}`
      };

      // Hacer llamada a la API de órdenes de compra
      const response = await fetch(`${API_BASE_URL}/locations/loc-1/purchase-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear orden de compra');
      }

      const result = await response.json();
      
      toast.success('Orden de compra creada exitosamente');
      return { success: true, data: result.data };
    } catch (err: any) {
      const errorMessage = err.message || 'Error al crear orden de compra';
      setError(errorMessage);
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Navegar páginas
  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  }, [pagination.totalPages]);

  // Refrescar datos
  const refresh = useCallback(() => {
    loadAllIngredients();
  }, [loadAllIngredients]);

  return {
    // Datos
    ingredients: paginatedIngredients,
    allIngredients,
    movements,
    alerts,
    stats,
    pagination,

    // Estado
    filters,
    loading,
    error,

    // Acciones
    updateFilters,
    createIngredient,
    updateIngredient,
    deleteIngredient,
    adjustStock,
    loadIngredientMovements,
    createPurchaseOrder,
    goToPage,
    refresh
  };
};